import {type _ActionsTree} from 'pinia';
import {type UserStoreState} from '@/store/user/state';
import type UserAccountRequest from '~~/package/apis/user/request/UserAccountRequest';
import {UserV2Model} from '~~/package/mapper/user/model/UserV2Model';
import {SubscriptionModel} from '~~/package/mapper/subscription/model/SubscriptionModel';
import {getCookie} from '~~/package/utils/CookieUtil';
import {UserV2Type} from '~~/package/mapper/user/model/UserType';
import type {UserStoreGetters} from '~/store/user/getters';
import {RouteName} from '~~/package/enum/RouteName';
import {CookieKeyType} from '~~/package/mapper/common/model/CookieKeyType';

export interface UserStoreActions extends _ActionsTree {
  firstNameChange: (firstName: string) => void;
  updateUserV2: (user: UserV2Model) => void;
  updateSubscription: (subscription: SubscriptionModel | null) => void;
  isLogin: () => boolean;
  isLogOut: () => boolean;
  changeAccountData: (userAccountRequest: UserAccountRequest) => void;
  changeProfileImageUrl: (userProfileImgUrl: string) => void;
  moveToRedirectUrl: () => void;
}

export const actions: UserStoreActions | UserStoreState | UserStoreGetters = {
  firstNameChange(firstName: string): void {
    (this as UserStoreState).firstName = firstName;
  },
  updateUserV2(user: UserV2Model): void {
    (this as UserStoreState).userV2 = user;
  },
  updateSubscription(subscription: SubscriptionModel | null): void {
    (this as UserStoreState).subscription = subscription;
  },
  changeAccountData(userAccountRequest: UserAccountRequest): void {
    (this as UserStoreState).userV2.fullName = userAccountRequest.nickName;
    (this as UserStoreState).userV2.location = userAccountRequest.location;
  },
  changeProfileImageUrl(userProfileImgUrl: string): void {
    (this as UserStoreState).userV2.profileImageUrl = userProfileImgUrl;
  },
  isLogin(): boolean {
    return !isEmptyString((this as UserStoreState).userV2.uuid);
  },
  isLogOut(): boolean {
    return isEmptyString((this as UserStoreState).userV2.uuid);
  },
  moveToRedirectUrl(): void {
    const router = useRouter();
    const raw = getCookie(CookieKeyType.REDIRECT_URL) as any;
    const fallback = () => router.replace({ path: "/dashboard" });

    if (!raw || !raw.path) {
      fallback();
      return;
    }

    const path = String(raw.path || "/dashboard").trim();

    if (!path.startsWith("/") || path.startsWith("//")) {
      fallback();
      return;
    }

    const query =
      raw.query && typeof raw.query === "object" && !Array.isArray(raw.query)
        ? raw.query
        : {};
    const name = raw.name ? String(raw.name) : undefined;
    const params =
      raw.params && typeof raw.params === "object" && !Array.isArray(raw.params)
        ? raw.params
        : {};
    const isManager = !!raw.isManager;

    if (isManager) {
      const grade = (this as UserStoreState).userV2.orgInfo?.grades?.[0];

      const isAdmin =
        grade === UserV2Type.ORG_SUPER_ADMINISTRATOR ||
        grade === UserV2Type.ORG_ADMINISTRATOR;

      if (!isAdmin) {
        fallback();
        return;
      }
    }

    if (name) {
      router.replace({ name: name as any, params, query });
      return;
    }

    router.replace({ path, query });
  }
};
